import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard, Menu, X } from 'lucide-react'
import { BrandLogo } from '@/components/ui/brand-logo'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { recordSiteEvent } from '@/lib/site-metrics'

interface PublicHeaderProps {
  /** When true: absolute + transparent (for hero overlays). Default: sticky + dark bg. */
  transparent?: boolean
}

const MOBILE_MENU_ID = 'public-mobile-menu'

const navLinkClass =
  'shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-[var(--radius-md)] no-underline transition-colors focus-ring text-[var(--text-public-secondary)] hover:text-[var(--text-public-primary)] hover:bg-[var(--surface-on-dark-hover)]'
const mobileLinkClass =
  'block py-2 px-3 text-sm font-medium rounded-[var(--radius-xl)] no-underline transition-colors focus-ring text-[var(--text-public-secondary)] hover:text-[var(--text-public-primary)] hover:bg-[var(--surface-on-dark-hover)]'

export function PublicHeader({ transparent = false }: PublicHeaderProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header
      className={cn(
        'chrome-on-dark',
        // `transparent` means NO CHROME — no background, no border. It used to
        // also mean `absolute`, and that was the bug: taken out of flow, the
        // header reserved no space, so whatever followed it started at y=0 and
        // rendered underneath it. On the landing page that put the hero eyebrow
        // pill straight through the wordmark.
        //
        // It is in flow now. The only caller is the landing hero, where this
        // header is the first child of the section that owns the shaders — and
        // those are `absolute inset-0` on that section, so they still run
        // full-bleed BEHIND a transparent header. Identical visual, and a
        // collision is no longer possible to write.
        transparent
          ? 'relative z-[var(--z-nav)]'
          : 'sticky top-0 z-[var(--z-nav)] bg-[var(--surface-public)]/90 backdrop-blur-md border-b border-[var(--hairline-on-dark)]'
      )}
      // Push content below the iOS notch / status bar / Dynamic Island in PWA
      // standalone mode (viewport-fit=cover). Matches AttendeeHeader + topbar.
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-[var(--content-width-wide)] mx-auto h-[var(--nav-height)] flex items-center justify-between px-4 sm:px-6">
        {/* `wordmarkFrom="sm"` is the 320px width budget — see BrandLogo's prop
            docs for the measurement. This header is the tightest one in the app:
            it carries a lockup, a language switcher, a full-length conversion
            CTA and a menu button on one fixed-height line. */}
        <BrandLogo size="md" tone="onDark" wordmarkFrom="sm" />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          <Link to="/pricing" className={navLinkClass}>
            {t('nav.pricing')}
          </Link>
          <Link to="/about" className={navLinkClass}>
            {t('nav.about')}
          </Link>
          <Link to="/contact" className={navLinkClass}>
            {t('nav.contact')}
          </Link>
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          {/* D-språk (David, 2026-07-28): the RECIPIENT's browser decides the
              language of a public page. Detection is already `localStorage →
              navigator`, so that holds by construction — but this header carries
              the public OFFER view, where the recipient is a stranger who never
              chose anything and had no way to override the guess. A browser set
              to neither nb nor en falls back to Norwegian and was stuck there.
              The switcher is the same component the attendee header uses; it
              writes to localStorage, so the choice survives the next visit. */}
          <LanguageSwitcher tone="onDark" />
          {user ? (
            <Button asChild variant="public" size="sm">
              <Link to="/dashboard">
                <LayoutDashboard className="size-4" aria-hidden="true" />
                {t('nav.dashboard')}
              </Link>
            </Button>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden lg:inline-flex items-center px-5 py-2 text-sm font-medium text-[var(--text-public-primary)] border border-[var(--border-on-dark)] hover:border-[var(--border-on-dark-strong)] rounded-[var(--btn-radius)] no-underline transition-colors focus-ring"
              >
                {t('auth.login')}
              </Link>
              <Button asChild variant="public" size="sm">
                <Link to="/signup" onClick={() => recordSiteEvent('cta_click', 'header')}>{t('auth.signup')}</Link>
              </Button>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            /* `shrink-0`: this is a plain <button>, so it does not inherit the
               Button primitive's no-shrink rule, and it is LAST in the row —
               the item a squeeze pushes off the edge first. Measured at 320px
               before the lockup change: rendered 16px wide instead of 36 and
               clipped at the viewport. */
            className="hit-area lg:hidden shrink-0 rounded-full p-2 text-[var(--text-public-secondary)] hover:text-[var(--text-public-primary)] hover:bg-[var(--surface-on-dark-hover)] transition-colors cursor-pointer focus-ring"
            aria-label={t(mobileMenuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
            aria-expanded={mobileMenuOpen}
            aria-controls={MOBILE_MENU_ID}
          >
            {mobileMenuOpen ? <X className="size-5" aria-hidden="true" /> : <Menu className="size-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <nav
          id={MOBILE_MENU_ID}
          className="lg:hidden border-t border-[var(--hairline-on-dark)] bg-[var(--surface-public)]/95 backdrop-blur-md px-4 sm:px-6 py-3 space-y-1"
        >
          <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>
            {t('nav.pricing')}
          </Link>
          <Link to="/about" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>
            {t('nav.about')}
          </Link>
          <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>
            {t('nav.contact')}
          </Link>
          {!user && (
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>
              {t('auth.login')}
            </Link>
          )}
        </nav>
      )}
    </header>
  )
}
